import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { brandConfig } from '../../../config/brands';
import { apiClient } from '../../../services/apiClient';
import { useMaterialsPanelStore } from '../../../stores/materialsPanelStore';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { useToast } from '../../../hooks/useToast';
import type { ProductLinkInput } from '../../../features/materials/api';
import { appPath, hardNavigate } from '../../../utils/navigation';
import { AddProductChooserModal } from './AddProductChooserModal';
import { AsinSearchModal } from './AsinSearchModal';
import { ManualProductFormModal } from './ManualProductFormModal';
import modalStyles from './MaterialPreviewModal.module.css';
import styles from './ProductSelectorModal.module.css';

type MobileStep = 'brand' | 'product';
type SourceTab = 'amazon' | 'manual';

/**
 * 商品セレクタが Amazon 商品 (`amazon_products`) と手動登録商品 (`products`) を
 * 同じリストに並べるために正規化した表示用ビュー。
 *
 * NOTE: `features/products/types.ts::Product` (手動登録商品) とは別物。
 *       `source` の取り得る値も異なる ('amazon' | 'manual' vs 'manual' | 'csv' | 'excel')。
 */
interface SelectableProduct {
  id: string; // UUID (amazon_products.id or products.id)
  asin?: string; // Amazon products only
  sku?: string; // Manual products
  title?: string;
  brand_name?: string;
  brand_id?: string;
  main_image_url?: string;
  source: SourceTab;
}

/** 選択済み商品を一意に識別するキー */
function selectionKey(productType: SourceTab, productId: string): string {
  return `${productType}:${productId}`;
}

function parseSelectionKey(key: string): ProductLinkInput {
  const [product_type, product_id] = key.split(':') as [SourceTab, string];
  return { product_type, product_id };
}

interface ProductSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** @deprecated 後方互換用: ASIN文字列配列を返す */
  onSelect?: (asins: string[]) => void;
  /** 商品リンク入力を返す（title等の追加情報含む） */
  onSelectLinks?: (links: (ProductLinkInput & { title?: string; asin?: string; main_image_url?: string })[]) => void;
  /** @deprecated */
  initialSelectedAsins?: string[];
  /** 初期選択済みリンク */
  initialSelectedLinks?: ProductLinkInput[];
  maxSelection?: number;
  /**
   * 表示対象を指定リンクに該当する商品だけに絞る。
   * 一括解除時など「既に紐づいている商品からのみ選ばせたい」ユースケースで使う。
   */
  restrictToLinks?: ProductLinkInput[];
  /** 確定ボタンのラベル。指定すると "{label} ({count})" 形式で描画。既定: "選択を確定" */
  confirmLabel?: string;
}

export function ProductSelectorModal({
  isOpen,
  onClose,
  onSelect,
  onSelectLinks,
  initialSelectedAsins = [],
  initialSelectedLinks = [],
  maxSelection,
  restrictToLinks,
  confirmLabel = '選択を確定',
}: ProductSelectorModalProps) {
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualAsinInput, setManualAsinInput] = useState('');
  // 「+ 商品を追加」から起動するネストモーダル群 (計画書§4.2)。
  // ProductSelectorModal は isOpen:false で unmount するため、単一選択モードでも
  // 親を閉じずにここで重ねて表示する (showManualInput と同じオーバーレイパターン)。
  const [addProductModal, setAddProductModal] = useState<'chooser' | 'asin' | 'manual' | null>(null);
  const [mobileStep, setMobileStep] = useState<MobileStep>('brand');
  const [sourceFilter, setSourceFilter] = useState<SourceTab | 'all'>('all');
  const closeMaterialsPanel = useMaterialsPanelStore((state) => state.close);
  const isMobile = useIsMobile();
  const toast = useToast();
  const touchStartX = useRef<number | null>(null);

  // モバイル: スワイプダウンで閉じるジェスチャー
  const [sheetDragging, setSheetDragging] = useState(false);
  const [sheetDragOffset, setSheetDragOffset] = useState(0);
  const sheetSwipeStartRef = useRef(0);

  const handleSheetSwipeStart = useCallback((e: React.TouchEvent) => {
    sheetSwipeStartRef.current = e.touches[0].clientY;
    setSheetDragging(true);
  }, []);

  const handleSheetSwipeMove = useCallback((e: React.TouchEvent) => {
    if (!sheetDragging) return;
    const deltaY = e.touches[0].clientY - sheetSwipeStartRef.current;
    if (deltaY > 0) {
      setSheetDragOffset(deltaY);
    }
  }, [sheetDragging]);

  const handleSheetSwipeEnd = useCallback(() => {
    setSheetDragging(false);
    if (sheetDragOffset > 100) {
      onClose();
    }
    setSheetDragOffset(0);
  }, [sheetDragOffset, onClose]);

  useLockBodyScroll(isOpen && isMobile);

  // 単一選択モード: チェックボックスなし、クリックで即確定
  const isSingleSelectMode = maxSelection === 1;

  // Amazon商品を取得
  const { data: amazonData, isLoading: isLoadingAmazon } = useQuery({
    queryKey: ['products', 'amazon', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('/amazon/products', {
        params: { limit: 1000 }
      });
      return response.data;
    },
    enabled: isOpen,
    staleTime: 0,
  });

  // マニュアル商品を取得
  const { data: manualData, isLoading: isLoadingManual } = useQuery({
    queryKey: ['products', 'manual', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('/products', {
        params: { limit: 1000 }
      });
      return response.data;
    },
    enabled: isOpen,
    staleTime: 0,
  });

  const isLoading = isLoadingAmazon || isLoadingManual;

  // お気に入りブランドを取得
  const queryClient = useQueryClient();
  const { data: favoritesData } = useQuery({
    queryKey: ['user', 'favorite-brands'],
    queryFn: async () => {
      const response = await apiClient.get('/user/favorite-brands');
      return response.data as { favorites: { brand_id: string | null; brand_name: string | null }[] };
    },
    enabled: isOpen,
    staleTime: 60_000,
  });

  /** お気に入りキーの Set。brandKey = brand_id || brand_name と同じロジック */
  const favoriteBrandKeys = useMemo(() => {
    const set = new Set<string>();
    for (const fav of favoritesData?.favorites || []) {
      const key = fav.brand_id || fav.brand_name;
      if (key) set.add(key);
    }
    return set;
  }, [favoritesData]);

  // お気に入りトグル (楽観的更新)
  const toggleFavoriteMutation = useMutation({
    mutationFn: async (params: { brandKey: string; brand_id: string | null; brand_name: string | null; isFavorite: boolean }) => {
      if (params.isFavorite) {
        const search = new URLSearchParams();
        if (params.brand_id) search.set('brand_id', params.brand_id);
        else if (params.brand_name) search.set('brand_name', params.brand_name);
        await apiClient.delete(`/user/favorite-brands?${search.toString()}`);
      } else {
        const payload: { brand_id?: string; brand_name?: string } = {};
        if (params.brand_id) payload.brand_id = params.brand_id;
        else if (params.brand_name) payload.brand_name = params.brand_name;
        await apiClient.post('/user/favorite-brands', payload);
      }
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ['user', 'favorite-brands'] });
      const prev = queryClient.getQueryData<{ favorites: { brand_id: string | null; brand_name: string | null }[] }>(['user', 'favorite-brands']);
      const nextFavorites = params.isFavorite
        ? (prev?.favorites || []).filter((f) => (f.brand_id || f.brand_name) !== params.brandKey)
        : [...(prev?.favorites || []), { brand_id: params.brand_id, brand_name: params.brand_id ? null : params.brand_name }];
      queryClient.setQueryData(['user', 'favorite-brands'], { favorites: nextFavorites });
      return { prev };
    },
    onError: (_err, _params, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['user', 'favorite-brands'], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'favorite-brands'] });
    },
  });

  // 統合商品リスト
  const amazonProducts: SelectableProduct[] = useMemo(
    () => (amazonData?.products || []).map((p: any) => ({
      id: p.id,
      asin: p.asin,
      title: p.title,
      brand_name: p.brand_name,
      brand_id: p.brand_id,
      main_image_url: p.main_image_url,
      source: 'amazon' as SourceTab,
    })),
    [amazonData],
  );

  const manualProducts: SelectableProduct[] = useMemo(
    () => (manualData?.products || []).map((p: any) => ({
      id: p.id,
      sku: p.sku,
      title: p.title,
      brand_name: p.brand_name,
      brand_id: p.brand_id,
      main_image_url: p.main_image_url,
      source: 'manual' as SourceTab,
    })),
    [manualData],
  );

  const allProducts = useMemo(() => {
    const merged = [...amazonProducts, ...manualProducts];
    if (!restrictToLinks) return merged;
    const allow = new Set(restrictToLinks.map((l) => selectionKey(l.product_type as SourceTab, l.product_id)));
    return merged.filter((p) => allow.has(selectionKey(p.source, p.id)));
  }, [amazonProducts, manualProducts, restrictToLinks]);

  // モーダルオープン時に初期選択をセット（isOpenがtrueに変わった時のみ）
  const prevIsOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      const keys = new Set<string>();

      // 新API: initialSelectedLinks
      for (const link of initialSelectedLinks) {
        keys.add(selectionKey(link.product_type as SourceTab, link.product_id));
      }

      // 後方互換: initialSelectedAsins → Amazon product idに解決
      if (initialSelectedAsins.length > 0 && amazonProducts.length > 0) {
        for (const asin of initialSelectedAsins) {
          const product = amazonProducts.find(p => p.asin === asin);
          if (product) {
            keys.add(selectionKey('amazon', product.id));
          }
        }
      }

      setSelectedKeys(keys);
      setMobileStep('brand');
    }
    prevIsOpen.current = isOpen;
  });

  // スマホ用: ブランド選択時に商品ステップへ
  const handleBrandSelect = useCallback((brandKey: string) => {
    setSelectedBrand(brandKey);
    if (isMobile) {
      setMobileStep('product');
    }
  }, [isMobile]);

  const NO_BRAND_KEY = '__no_brand__';

  // ブランドごとに商品をグループ化（全ソース統合）
  const brandGroups = useMemo(() => {
    const groups = new Map<string, SelectableProduct[]>();

    allProducts.forEach((product) => {
      const brandKey = product.brand_id || product.brand_name || NO_BRAND_KEY;
      if (!groups.has(brandKey)) {
        groups.set(brandKey, []);
      }
      groups.get(brandKey)!.push(product);
    });

    return Array.from(groups.entries())
      .map(([brandKey, prods]) => {
        const filteredCount = sourceFilter === 'all'
          ? prods.length
          : prods.filter(p => p.source === sourceFilter).length;
        const isFavorite = brandKey !== NO_BRAND_KEY && favoriteBrandKeys.has(brandKey);
        return {
          brandKey,
          brandName: brandKey === NO_BRAND_KEY ? 'ブランドなし' : (prods[0].brand_name || brandKey),
          brandId: prods[0].brand_id || null,
          fallbackBrandName: prods[0].brand_name || null,
          isFavorite,
          products: prods.sort((a, b) =>
            (a.title || '').localeCompare(b.title || '', 'ja')
          ),
          filteredCount,
        };
      })
      .sort((a, b) => {
        // ブランドなしは常に末尾
        if (a.brandKey === NO_BRAND_KEY) return 1;
        if (b.brandKey === NO_BRAND_KEY) return -1;
        // お気に入りを先頭に固める
        if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
        // 同グループ内は五十音順
        return a.brandKey.localeCompare(b.brandKey, 'ja');
      });
  }, [allProducts, sourceFilter, favoriteBrandKeys]);

  // 初回モーダル表示時、既存選択からブランドを自動選択（未選択時は先頭ブランド）
  useEffect(() => {
    if (isOpen && allProducts.length > 0 && !selectedBrand) {
      if (selectedKeys.size > 0) {
        const firstKey = Array.from(selectedKeys)[0];
        const { product_id } = parseSelectionKey(firstKey);
        const product = allProducts.find(p => p.id === product_id);
        if (product) {
          const brandKey = product.brand_id || product.brand_name || NO_BRAND_KEY;
          setSelectedBrand(brandKey);
          return;
        }
      }
      // 選択商品がない場合、先頭のブランドを自動選択
      if (brandGroups.length > 0) {
        setSelectedBrand(brandGroups[0].brandKey);
      }
    }
  }, [isOpen, allProducts, selectedKeys, selectedBrand, brandGroups]);

  // 選択中のブランドの商品リスト（フィルタ適用）
  const selectedBrandProducts = useMemo(() => {
    if (!selectedBrand) return [];
    const group = brandGroups.find(g => g.brandKey === selectedBrand);
    const prods = group?.products || [];
    if (sourceFilter === 'all') return prods;
    return prods.filter(p => p.source === sourceFilter);
  }, [selectedBrand, brandGroups, sourceFilter]);

  // 単一選択モード: 商品クリックで即確定
  const handleImmediateSelect = useCallback((product: SelectableProduct) => {
    const link: ProductLinkInput = {
      product_type: product.source,
      product_id: product.id,
    };
    const enriched = { ...link, title: product.title, asin: product.asin, main_image_url: product.main_image_url };

    if (onSelectLinks) {
      onSelectLinks([enriched]);
    } else if (onSelect && product.asin) {
      onSelect([product.asin]);
    }
    onClose();
  }, [onSelectLinks, onSelect, onClose]);

  // 商品選択トグル
  const toggleProduct = (product: SelectableProduct) => {
    // 単一選択モードの場合は即座に確定
    if (isSingleSelectMode) {
      handleImmediateSelect(product);
      return;
    }

    const key = selectionKey(product.source, product.id);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        if (maxSelection !== undefined && next.size >= maxSelection) {
          if (maxSelection === 1) {
            return new Set([key]);
          }
          return next;
        }
        next.add(key);
      }
      return next;
    });
  };

  const isProductSelected = (product: SelectableProduct) =>
    selectedKeys.has(selectionKey(product.source, product.id));

  // 手動入力ASINを追加 (Amazon products only)
  const handleAddManualAsins = () => {
    const asins = manualAsinInput
      .split(/[,\n]/)
      .map(s => s.trim())
      .filter(s => s.length > 0);

    if (asins.length === 0) return;

    setSelectedKeys(prev => {
      const next = new Set(prev);
      for (const asin of asins) {
        // ASIN→Amazon product idを解決
        const product = amazonProducts.find(p => p.asin === asin);
        if (product) {
          const key = selectionKey('amazon', product.id);
          if (maxSelection !== undefined) {
            if (maxSelection === 1) {
              return new Set([key]);
            }
            if (next.size >= maxSelection) break;
          }
          next.add(key);
        }
      }
      return next;
    });

    setManualAsinInput('');
    setShowManualInput(false);
  };

  // 保存
  const handleSave = () => {
    const links: ProductLinkInput[] = Array.from(selectedKeys).map(parseSelectionKey);

    // 新APIコールバック（タイトル・ASIN・画像URL情報付き）
    if (onSelectLinks) {
      const enriched = links.map(link => {
        const product = allProducts.find(p => p.id === link.product_id);
        return { ...link, title: product?.title, asin: product?.asin, main_image_url: product?.main_image_url };
      });
      onSelectLinks(enriched);
    } else {
      // 後方互換: onSelectLinksがない場合のみASINを返す
      const asins: string[] = [];
      for (const link of links) {
        if (link.product_type === 'amazon') {
          const product = amazonProducts.find(p => p.id === link.product_id);
          if (product?.asin) {
            asins.push(product.asin);
          }
        }
      }
      onSelect?.(asins);
    }

    onClose();
  };

  // 「マニュアル案件登録」への入口 (AddProductChooserModal 選択時 / amazonIntegration=false での
  // 直接起動時) の共通ゲート。quota 到達時はモーダルを開く前にブロックする (登録ボタンまで進んでから
  // 403 になるより早く伝わる)。
  const openManualProductModal = useCallback(() => {
    if (manualData?.quota?.can_create === false) {
      toast.error(`${brandConfig.labels.productShort}数がプランの上限に達しています`);
      return;
    }
    setAddProductModal('manual');
  }, [manualData, toast]);

  // ネストモーダル (AsinSearchModal / ManualProductFormModal) での新規登録完了時の共通処理 (計画書§4.2)。
  // useCreateProduct の invalidate 対象 (['manual-products']) はこのモーダルのリストクエリ
  // (['products','manual','all'] / ['products','amazon','all']) と別キーのため、ここで明示的に invalidate する。
  const handleProductRegistered = useCallback((
    source: SourceTab,
    registered: { id?: string; asin?: string; title?: string; main_image_url?: string },
  ) => {
    // リストへの反映 (invalidate) とネストモーダルを閉じる処理は id の有無に関わらず必ず行う。
    // ここで早期returnすると、成功トースト後にネストモーダルが開いたまま何も起きずユーザーが詰まる。
    queryClient.invalidateQueries({ queryKey: ['products', 'manual', 'all'] });
    queryClient.invalidateQueries({ queryKey: ['products', 'amazon', 'all'] });
    setAddProductModal(null);

    if (!registered.id) {
      // amazon経路 (account_id指定のSP-API) は必ずidを返す想定。念のための防御として、
      // id が無い場合は自動選択のみスキップする (リストには反映されるので手動で選択できる)。
      return;
    }

    const link: ProductLinkInput = { product_type: source, product_id: registered.id };
    const enriched = { ...link, title: registered.title, asin: registered.asin, main_image_url: registered.main_image_url };

    if (isSingleSelectMode) {
      // 単一選択モード: 登録済み商品をそのまま即時確定し、親モーダルも閉じる
      if (onSelectLinks) {
        onSelectLinks([enriched]);
      } else if (onSelect && registered.asin) {
        onSelect([registered.asin]);
      }
      onClose();
    } else {
      // 複数選択モード: 親モーダルに戻り、選択済み列に追加する（上限は尊重する）
      const key = selectionKey(source, registered.id);
      setSelectedKeys(prev => {
        if (prev.has(key)) return prev;
        if (maxSelection !== undefined) {
          if (maxSelection === 1) return new Set([key]);
          if (prev.size >= maxSelection) return prev;
        }
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    }
  }, [isSingleSelectMode, onSelectLinks, onSelect, onClose, maxSelection, queryClient]);

  // 選択済みキーから商品情報を検索
  const findProductByKey = (key: string): SelectableProduct | undefined => {
    const { product_id } = parseSelectionKey(key);
    return allProducts.find(p => p.id === product_id);
  };

  /** 商品の表示ラベル（ASINまたはSKU等） */
  const productSubLabel = (product: SelectableProduct): string => {
    if (product.asin) return `Amazon: ${product.asin}`;
    if (product.source === 'manual') return product.sku ? `マニュアル登録: ${product.sku}` : 'マニュアル登録';
    return '';
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className={modalStyles.backdrop}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        {/* Modal */}
        <div
          className={`${modalStyles.modal} ${styles.productSelectorModal} ${sheetDragging ? styles.sheetDragging : ''}`}
          style={isMobile && sheetDragOffset > 0 ? { transform: `translateY(${sheetDragOffset}px)` } : undefined}
        >
          {isMobile && (
            <div
              className={styles.dragHandle}
              onTouchStart={handleSheetSwipeStart}
              onTouchMove={handleSheetSwipeMove}
              onTouchEnd={handleSheetSwipeEnd}
            />
          )}
          <button
            type="button"
            className={`${modalStyles.closeButton} ${styles.closeButton}`}
            onClick={onClose}
            aria-label="閉じる"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div
            className={styles.header}
            onTouchStart={isMobile ? handleSheetSwipeStart : undefined}
            onTouchMove={isMobile ? handleSheetSwipeMove : undefined}
            onTouchEnd={isMobile ? handleSheetSwipeEnd : undefined}
          >
            <h2>{brandConfig.labels.productShort}を選択</h2>
            {maxSelection !== undefined && !isSingleSelectMode && (
              <div className={styles.selectionCount}>
                {selectedKeys.size} / {maxSelection} 選択中
              </div>
            )}
          </div>

          {/* マーケットフィルタ */}
          <div className={styles.filterBar}>
            <div className={styles.sourceFilterGroup}>
              {(['all', 'amazon', 'manual'] as const).map((val) => (
                <button
                  key={val}
                  type="button"
                  className={`${styles.sourceFilterBtn} ${sourceFilter === val ? styles.activeFilter : ''}`}
                  onClick={() => setSourceFilter(val)}
                >
                  {val === 'all' ? 'すべて' : val === 'amazon' ? 'Amazon' : 'マニュアル'}
                </button>
              ))}
            </div>
            <div className={styles.filterBarRight}>
              {!isSingleSelectMode && (
                <button
                  type="button"
                  className={styles.addProductBtn}
                  onClick={() => setShowManualInput(true)}
                >
                  ASIN直接入力
                </button>
              )}
              {/* restrictToLinks (一括解除モード) では新規登録商品は必ずフィルタ対象外になり
                  追加しても何も起きないため、ボタン自体を非表示にする (計画書§4.2/§5) */}
              {!restrictToLinks && (
                <button
                  type="button"
                  className={styles.addProductBtn}
                  onClick={() => brandConfig.features.amazonIntegration ? setAddProductModal('chooser') : openManualProductModal()}
                >
                  + {brandConfig.labels.productShort}を追加
                </button>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className={styles.loading}>読み込み中...</div>
          ) : allProducts.length === 0 ? (
            <div className={styles.emptyState}>
              <a
                href={appPath('/products')}
                className={styles.registerLink}
                onClick={(e) => {
                  e.preventDefault();
                  closeMaterialsPanel();
                  onClose();
                  hardNavigate('/products');
                }}
              >
                {brandConfig.labels.products}登録を行なってください
              </a>
            </div>
          ) : (
            <>
            <div
              className={`${styles.columnsContainer} ${isMobile ? styles.mobileSlider : ''}`}
              data-step={mobileStep}
              onTouchStart={(e) => {
                if (isMobile && mobileStep === 'product') {
                  touchStartX.current = e.touches[0].clientX;
                }
              }}
              onTouchEnd={(e) => {
                if (isMobile && mobileStep === 'product' && touchStartX.current !== null) {
                  const deltaX = e.changedTouches[0].clientX - touchStartX.current;
                  if (deltaX > 50) {
                    setMobileStep('brand');
                  }
                  touchStartX.current = null;
                }
              }}
            >

              {/* ブランド列 */}
              <div className={`${styles.column} ${styles.brandColumn}`}>
              <div className={styles.columnHeader}>ブランド</div>
              <div className={styles.columnList}>
                {brandGroups.map(({ brandKey, brandName, brandId, fallbackBrandName, isFavorite, filteredCount }) => {
                  const canFavorite = brandKey !== NO_BRAND_KEY;
                  return (
                  <div
                    key={brandKey}
                    className={`${styles.listItem} ${selectedBrand === brandKey ? styles.selected : ''} ${filteredCount === 0 ? styles.dimmed : ''}`}
                    onClick={() => handleBrandSelect(brandKey)}
                    title={brandName}
                  >
                    <span className={styles.itemLabel}>
                      {brandName.includes('(') ? (
                        <>
                          <span className={styles.itemLabelMain}>
                            {brandName.split('(')[0].trim()}
                            {canFavorite && (
                              <button
                                type="button"
                                className={styles.favoriteButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavoriteMutation.mutate({
                                    brandKey,
                                    brand_id: brandId,
                                    brand_name: fallbackBrandName,
                                    isFavorite,
                                  });
                                }}
                                aria-label={isFavorite ? 'お気に入りから外す' : 'お気に入りに追加'}
                                title={isFavorite ? 'お気に入りから外す' : 'お気に入りに追加'}
                              >
                                <img
                                  src={isFavorite ? '/star_1_gold.svg' : '/star_1.svg'}
                                  alt=""
                                  className={`${styles.favoriteIcon} ${isFavorite ? '' : styles.favoriteIconEmpty}`}
                                />
                              </button>
                            )}
                          </span>
                          <br />
                          <span className={styles.itemLabelSub}>
                            {brandName.split('(')[1]?.replace(/\)$/, '')}
                          </span>
                        </>
                      ) : (
                        <>
                          {brandName}
                          {canFavorite && (
                            <button
                              type="button"
                              className={styles.favoriteButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavoriteMutation.mutate({
                                  brandKey,
                                  brand_id: brandId,
                                  brand_name: fallbackBrandName,
                                  isFavorite,
                                });
                              }}
                              aria-label={isFavorite ? 'お気に入りから外す' : 'お気に入りに追加'}
                              title={isFavorite ? 'お気に入りから外す' : 'お気に入りに追加'}
                            >
                              <img
                                src={isFavorite ? '/star_1_gold.svg' : '/star_1.svg'}
                                alt=""
                                className={`${styles.favoriteIcon} ${isFavorite ? '' : styles.favoriteIconEmpty}`}
                              />
                            </button>
                          )}
                        </>
                      )}
                    </span>
                    <span className={styles.itemCount}>({filteredCount})</span>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* 商品列 */}
            <div className={styles.column}>
              <div className={styles.columnHeader}>
                {brandConfig.labels.productShort}
                {isMobile && mobileStep === 'product' && (
                  <button
                    type="button"
                    className={styles.columnHeaderClose}
                    onClick={() => setMobileStep('brand')}
                  >
                    閉じる
                  </button>
                )}
              </div>
              <div className={styles.columnList}>
                {selectedBrandProducts.map((product) => (
                  <div
                    key={product.id}
                    className={`${styles.listItem} ${!isSingleSelectMode && isProductSelected(product) ? styles.checked : ''} ${isSingleSelectMode ? styles.singleSelectItem : ''}`}
                    onClick={() => toggleProduct(product)}
                  >
                    {!isSingleSelectMode && (
                      <input
                        type="checkbox"
                        checked={isProductSelected(product)}
                        onChange={() => {}}
                        className={styles.checkbox}
                      />
                    )}
                    {product.main_image_url && (
                      <img
                        src={product.main_image_url}
                        alt={product.title || product.asin || product.id}
                        className={styles.productThumbnail}
                      />
                    )}
                    <div className={styles.productInfo}>
                      <div
                        className={styles.productTitle}
                        title={product.title || product.asin || product.id}
                      >
                        {product.title || product.asin || product.id}
                      </div>
                      <div className={styles.productAsin}>{productSubLabel(product)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 選択済み列（単一選択モードでは非表示） */}
            {!isSingleSelectMode && (
              <div className={styles.column}>
                <div className={styles.columnHeader}>
                  選択済み ({selectedKeys.size})
                </div>
                <div className={styles.columnList}>
                  {Array.from(selectedKeys).map((key) => {
                    const product = findProductByKey(key);
                    const { product_type } = parseSelectionKey(key);
                    return (
                      <div
                        key={key}
                        className={styles.listItem}
                        onClick={() => product && toggleProduct(product)}
                      >
                        <button
                          type="button"
                          className={styles.removeButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (product) toggleProduct(product);
                          }}
                        >
                          ✕
                        </button>
                        {product?.main_image_url && (
                          <img
                            src={product.main_image_url}
                            alt={product?.title || product?.id}
                            className={styles.productThumbnail}
                          />
                        )}
                        <div className={styles.productInfo}>
                          <div
                            className={styles.productTitle}
                            title={product?.title || product?.id || key}
                          >
                            {product?.title || product?.id || key}
                          </div>
                          <div className={styles.productAsin}>
                            {product ? productSubLabel(product) : ''}
                            {product_type === 'manual' && (
                              <span className={styles.sourceLabel}> [マニュアル]</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          </>
        )}

          {/* Footer: 単一選択モード (クリックで即確定 + 右上 X で閉じる) ではフッタ自体を出さない */}
          {!isSingleSelectMode && (
            <div className={modalStyles.actionBar}>
              <button
                type="button"
                className={`btn-modal-secondary ${styles.cancelButton}`}
                onClick={onClose}
              >
                キャンセル
              </button>
              <div className={styles.actionBarRight}>
                <button
                  type="button"
                  className="btn-modal btn-modal-accent"
                  onClick={handleSave}
                >
                  {confirmLabel} ({selectedKeys.size})
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ASIN直接入力モーダル（標準の中央ネストモーダル） */}
      {showManualInput && (
        <div
          className="modal-overlay modal-overlay--nested"
          onClick={() => {
            setShowManualInput(false);
            setManualAsinInput('');
          }}
        >
          <div
            className="modal-card modal-card--sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <span className="modal-title">ASIN直接入力</span>
              <button
                type="button"
                className="modal-close-button"
                aria-label="閉じる"
                onClick={() => {
                  setShowManualInput(false);
                  setManualAsinInput('');
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <textarea
                className={styles.manualInputTextarea}
                value={manualAsinInput}
                onChange={(e) => setManualAsinInput(e.target.value)}
                placeholder="ASINを入力（カンマ区切りまたは改行）&#10;例: B0C1234567, B0D7891234"
                rows={6}
                autoFocus
              />
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-modal-secondary"
                  onClick={() => {
                    setShowManualInput(false);
                    setManualAsinInput('');
                  }}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="btn-modal"
                  onClick={handleAddManualAsins}
                  disabled={!manualAsinInput.trim()}
                >
                  追加
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 「+ 商品を追加」から起動するネストモーダル群（計画書§4.2） */}
      {addProductModal === 'chooser' && (
        <AddProductChooserModal
          onClose={() => setAddProductModal(null)}
          onSelectAsin={() => setAddProductModal('asin')}
          onSelectManual={openManualProductModal}
        />
      )}

      {addProductModal === 'asin' && (
        <AsinSearchModal
          onClose={() => setAddProductModal(null)}
          onRegistered={(product) => handleProductRegistered('amazon', product)}
        />
      )}

      {addProductModal === 'manual' && (
        <ManualProductFormModal
          onClose={() => setAddProductModal(null)}
          onRegistered={(product) => handleProductRegistered('manual', product)}
        />
      )}
    </>
  );

  return createPortal(modalContent, document.body);
}
